import { ReactNode } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Moon, Sun, Hash } from 'lucide-react';
import { Button } from './Button';
import { translations } from '../i18n/hu';

interface LayoutProps {
  children: ReactNode;
  currentTab: string;
  onTabChange: (tab: string) => void;
}

export function Layout({ children, currentTab, onTabChange }: LayoutProps) {
  const { theme, toggleTheme } = useTheme();

  const tabs = [
    { id: 'text', label: translations.nav.textHash },
    { id: 'file', label: translations.nav.fileHash },
    { id: 'verify', label: translations.nav.verify },
    { id: 'batch', label: translations.nav.batch },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Hash className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  {translations.app.title}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {translations.app.subtitle}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={toggleTheme}>
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  currentTab === tab.id
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            {translations.info.clientSide}
          </p>
        </div>
      </footer>
    </div>
  );
}

